import { useState, useRef } from "react";
import { Initiative } from "@/types";
import { useFieldConfig } from "@/hooks/useFieldConfig";
import { useLabsFeatures } from "@/hooks/useLabsFeatures";
import { StatusDot } from "@/components/ui/Badge";
import { LinkIcon, ChatBubbleLeftIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { ICON_MAP } from "@/components/modals/IconPicker";
import { formatDateRange } from "@/utils/dateRange";

interface InitiativeCardProps {
  initiative: Initiative;
  onClick?: () => void;
  quarterSpan?: number; // Number of quarters this initiative spans
}

export function InitiativeCard({ initiative, onClick, quarterSpan = 1 }: InitiativeCardProps) {
  const fc = useFieldConfig();
  const labs = useLabsFeatures();
  const classification = initiative.classification;
  const classificationVisible = fc.isVisible('classification');
  const status = initiative.status;
  const outgoingCount = initiative.dependencies?.length || 0;
  const reverseCount = (initiative as any)._count?.dependents || 0;
  const hasDependencies = labs.isEnabled('dependencies') && (outgoingCount > 0 || reverseCount > 0);
  const dependencyTooltip =
    outgoingCount > 0 && reverseCount > 0
      ? `Depends on ${outgoingCount} · ${reverseCount} depend on this`
      : outgoingCount > 0
        ? `Depends on ${outgoingCount} initiative${outgoingCount === 1 ? '' : 's'}`
        : `${reverseCount} initiative${reverseCount === 1 ? '' : 's'} depend on this`;
  const commentCount = (initiative as any)._count?.comments || 0;
  const isStarred = (initiative as any).isStarred === true;

  const titleRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // Filter tags that should display as icon on card
  const displayTags = fc.isVisible('tags')
    ? initiative.tags?.filter((initTag) => initTag.tag?.displayAsIcon && initTag.tag?.icon) || []
    : [];

  // Date-mode initiatives show their exact start/end dates on the pill (month-mode: nothing).
  const dateRange =
    (initiative as any).datePrecision === 'day'
      ? formatDateRange(initiative.startDate, initiative.endDate)
      : null;

  // How many tag icons fit before the "+N" overflow — scales with the pill's width (span).
  // Narrow single-column pills show only 2; wider bars have room for more.
  const maxIcons = quarterSpan <= 1 ? 2 : quarterSpan === 2 ? 4 : 5;

  // Measure on hover only — avoids one ResizeObserver per card at scale. Also open the tooltip
  // for narrow (single-quarter) date-mode pills so their dates are still discoverable.
  const handleTitleMouseEnter = () => {
    const el = titleRef.current;
    if (el && el.scrollWidth > el.clientWidth) setShowTooltip(true);
    else if (dateRange && quarterSpan === 1) setShowTooltip(true);
  };

  return (
    <div
      onClick={onClick}
      className="group relative h-full cursor-pointer"
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* The visible bar clips its own content so icons never cross the rounded outline. */}
      <div
        className="flex flex-col justify-center gap-0.5 rounded-lg px-3 py-1 h-full overflow-hidden transition-all group-hover:shadow-md"
        style={{
          backgroundColor: classificationVisible ? `${classification?.color || "#3B82F6"}15` : "#F3F4F615",
          borderWidth: "1px",
          borderColor: classificationVisible ? `${classification?.color || "#3B82F6"}40` : "#D1D5DB40",
          borderLeftWidth: "3px",
          borderLeftColor: classificationVisible ? (classification?.color || "#3B82F6") : "#D1D5DB",
        }}
      >
        {/* Line 1: title (own full-width line so it stays readable) + star */}
        <div className="flex items-center gap-1 min-w-0">
          <span
            ref={titleRef}
            className="font-semibold text-text-primary text-[13px] leading-tight truncate min-w-0 flex-1"
            onMouseEnter={handleTitleMouseEnter}
          >
            {initiative.title}
          </span>
          {isStarred && <StarSolid className="w-3 h-3 text-amber-400 flex-shrink-0" />}
        </div>

        {/* Line 2: status + date + tags + indicators (details in the hover popover) */}
        <div className="flex items-center gap-1.5 min-w-0" onMouseEnter={() => setShowTooltip(true)}>
          {status && (
            <div className="flex items-center gap-1 min-w-0">
              <StatusDot color={status.color} />
              <span className="text-[11px] text-text-secondary truncate">{status.name}</span>
            </div>
          )}

          {dateRange && (
            <span className="text-[11px] text-text-secondary whitespace-nowrap flex-shrink-0">
              {dateRange}
            </span>
          )}

          <div className="flex items-center gap-1 flex-shrink-0">
            {displayTags.slice(0, maxIcons).map((initTag) => {
              const IconComponent = initTag.tag?.icon ? ICON_MAP[initTag.tag.icon] : null;
              if (!IconComponent) return null;
              return <IconComponent key={initTag.tagId} className="w-3.5 h-3.5 text-text-secondary" />;
            })}
            {displayTags.length > maxIcons && (
              <span className="text-xs text-text-tertiary">+{displayTags.length - maxIcons}</span>
            )}
            {hasDependencies && <LinkIcon className="w-3.5 h-3.5 text-text-secondary" />}
            {commentCount > 0 && (
              <span className="flex items-center gap-0.5 text-text-secondary">
                <ChatBubbleLeftIcon className="w-3.5 h-3.5" />
                <span className="text-xs">{commentCount > 99 ? '99+' : commentCount}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Rich hover popover — rendered OUTSIDE the clipped bar so it is never cut off */}
      {showTooltip && (
        <div className="absolute left-0 bottom-full mb-1 z-[100] px-3 py-2 bg-gray-900 text-white rounded shadow-lg max-w-xs whitespace-normal">
          <div className="text-sm font-medium">{initiative.title}</div>
          <div className="mt-0.5 flex flex-col gap-0.5 text-xs text-gray-300">
            {status && <span>{status.name}</span>}
            {dateRange && <span>{dateRange}</span>}
            {displayTags.length > 0 && (
              <span>{displayTags.map((t) => t.tag?.name).filter(Boolean).join(', ')}</span>
            )}
            {hasDependencies && <span>{dependencyTooltip}</span>}
            {commentCount > 0 && <span>{commentCount} comment{commentCount !== 1 ? 's' : ''}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
